/**
 * The language and currency controls that live in the footer.
 *
 * ── WHY A NATIVE `<select>` ─────────────────────────────────────────────────
 * A custom listbox is roughly 200 lines of roving tabindex, `aria-activedescendant`,
 * type-ahead and outside-click handling, and it is worse on every phone than
 * the wheel iOS and Android already give you. The native control is keyboard
 * reachable, screen-reader labelled, correctly sized for touch, and free. This
 * phase is structure, not decoration, and the structurally correct answer to
 * "choose one of eight" is `<select>`.
 *
 * ── THE PART THAT IS EASY TO GET WRONG ──────────────────────────────────────
 * Changing the language re-renders the footer, which destroys the `<select>`
 * the user was interacting with. Focus would land on `<body>`, which for a
 * keyboard or screen-reader user means being silently dumped at the top of the
 * document mid-task. So the controls carry stable ids and the footer restores
 * focus after a rebuild — see `renderInto` in `footer.ts`.
 */
import {
  LOCALES,
  SUPPORTED_LOCALES,
  getLocale,
  localeMeta,
  setLocale,
  t,
  type Locale,
} from '../i18n';
import { el, on } from './dom';
import {
  BASE_CURRENCY,
  currencySnapshot,
  isSupportedCurrency,
  setCurrency,
  subscribeCurrency,
  type CurrencySnapshot,
} from './currency';

/** Stable ids, so focus can be restored across a re-render. */
export const LANGUAGE_SELECT_ID = 'site-language-select';
export const CURRENCY_SELECT_ID = 'site-currency-select';

/** A control plus the listener teardown it owns. */
export interface SwitcherHandle {
  readonly element: HTMLElement;
  destroy(): void;
}

const FIELD_CLASS =
  'flex flex-col gap-1 text-[13px] text-text-dim';
/*
 * Empty on purpose: `.rga-select` (engine/ui/controls.ts) draws the plate, the
 * 44px minimum and the chevron for every select in the product, so a second
 * set of utilities here would be a divergent copy of the same control.
 */
const SELECT_CLASS = '';

/**
 * Language `<select>`, every option written in its own language.
 *
 * Endonyms, not translations: a Turkish speaker looking for their language
 * scans for "Türkçe", and a list rendered in the CURRENT language is useless
 * to exactly the person who needs to change it — someone who cannot read the
 * current one.
 */
export function createLanguageSwitcher(): SwitcherHandle {
  const select = el('select', {
    id: LANGUAGE_SELECT_ID,
    class: SELECT_CLASS,
    name: 'locale',
    'aria-label': t('a11y.language_switcher'),
    autocomplete: 'off',
  });

  const activeLocale = getLocale();
  for (const locale of SUPPORTED_LOCALES) {
    const descriptor = LOCALES[locale];
    select.appendChild(
      el('option', {
        value: locale,
        lang: descriptor.tag,
        selected: locale === activeLocale,
        text: descriptor.native,
      }),
    );
  }
  select.value = activeLocale;

  const offChange = on(select, 'change', () => {
    const next = select.value as Locale;
    if (!SUPPORTED_LOCALES.includes(next)) return;
    const previous = activeLocale;
    // `setLocale` REJECTS when the catalogue chunk fails to load, leaving the
    // old language in place. Snapping the control back keeps it honest rather
    // than showing a language the page is not actually in.
    void setLocale(next).catch(() => {
      select.value = previous;
    });
  });

  const field = el('div', { class: FIELD_CLASS }, [
    el('label', { class: 'font-medium', for: LANGUAGE_SELECT_ID, text: t('common.language') }),
    el('div', { class: 'rga-select' }, [select]),
    machineTranslationNotice(),
  ]);

  return { element: field, destroy: offChange };
}

/**
 * The honesty note the i18n catalogue ships for exactly this purpose.
 *
 * Seven of the eight catalogues are machine translated and unreviewed. Showing
 * a player a wall of confident-sounding text that nobody who speaks their
 * language has read, with no indication of that, is the kind of small
 * dishonesty that costs trust when they notice — and they do notice. It renders
 * only when the ACTIVE catalogue says `machine`, so English never sees it.
 */
function machineTranslationNotice(): HTMLElement | null {
  if (localeMeta().translationStatus !== 'machine') return null;
  return el('p', {
    class: 'max-w-[28rem] text-[12px] leading-snug text-text-dim/80',
    text: t('settings.machine_translation_notice'),
  });
}

/**
 * Currency control — a `<select>` when this deployment enables more than one,
 * a plain indicator when it does not.
 *
 * A one-option select is a lie about agency: it looks interactive, takes a tab
 * stop, and does nothing. A deployment that enables EUR only gets the
 * indicator; this one enables the five currencies the pack ladder is priced in
 * (`CURRENCIES=EUR,USD,GBP,AUD,PLN` in `.env`, and `GET /i18n/config` on the
 * running backend returns exactly those five), so the select is what a player
 * actually sees.
 *
 * ── WHY THIS SUBSCRIBES, AND WHAT WAS WRONG WITHOUT IT ──────────────────────
 * It used to read `currencySnapshot()` ONCE, at construction, and never look
 * again. That is fine in the footer, which rebuilds its switchers on every
 * locale change, and it is broken on the settings screen, which deliberately
 * builds them once and keeps them for the life of the screen
 * (`meta/settings-screen.ts` — rebuilding would drop the focus a player is
 * holding). Two consequences, both real:
 *
 *  · `initCurrency()` resolves ASYNCHRONOUSLY. Mount the screen before
 *    `GET /i18n/config` comes back and the control is built from
 *    `FALLBACK_CURRENCIES` — EUR/GBP/USD — so AUD and PLN never appear at all,
 *    and a single-currency fallback would render the dead INDICATOR branch
 *    permanently on a deployment that offers five.
 *  · a currency set anywhere else (the footer's own switcher, the account
 *    adopted at sign-in) left this select showing the old code while the rest
 *    of the app used the new one.
 *
 * So the field is repainted from the store. The `<select>` is rebuilt only when
 * the OPTION LIST changes — never on a mere value change — because replacing
 * the element under a player who is holding it open would throw their focus to
 * `<body>` mid-choice, which is the failure the file header is about.
 *
 * ── THE NOTE UNDER THE SELECT IS NOT DECORATION ─────────────────────────────
 * The moment this control offers a second currency it starts implying the
 * player can be CHARGED in it, and they cannot: every charge is EUR
 * (`checkoutAvailability` in `backend/src/game/shop/packs.service.ts` reports
 * `available: false` with `shop.packs.checkout.currency_preview` for any other
 * shelf). `settings.currency_help` is the catalogue's sentence for exactly
 * that, and it renders under the SELECT branch only — a one-currency
 * deployment shows and bills the same code, so the sentence would be warning
 * about a mismatch that cannot happen there.
 */
export function createCurrencySwitcher(): SwitcherHandle {
  const field = el('div', { class: FIELD_CLASS });

  /** The option codes currently painted, so a repaint can be skipped. */
  let paintedCodes = '';
  /** Teardown for the live `<select>`, if the select branch is the one drawn. */
  let offChange: (() => void) | null = null;
  let select: HTMLSelectElement | null = null;
  let indicator: HTMLElement | null = null;

  const note = (): HTMLElement =>
    el('p', {
      class: 'max-w-[28rem] text-[12px] leading-snug text-text-dim/80',
      'data-testid': 'currency-billing-note',
      text: t('settings.currency_help', { base: BASE_CURRENCY }),
    });

  const buildIndicator = (snapshot: CurrencySnapshot): void => {
    indicator = el('span', {
      class: 'text-text',
      'data-testid': 'currency-indicator',
      text: displayCurrency(snapshot.currency, snapshot),
    });
    field.classList.add('gap-0.5');
    // NO billing note here, deliberately: a deployment that enables one
    // currency shows and bills the same one, so the sentence would be warning
    // a player about a mismatch that cannot occur. `currency-brand.test.ts`
    // asserts its absence.
    field.replaceChildren(
      el('span', { class: 'font-medium', text: t('common.currency') }),
      indicator,
    );
  };

  const buildSelect = (snapshot: CurrencySnapshot): void => {
    const control = el('select', {
      id: CURRENCY_SELECT_ID,
      class: SELECT_CLASS,
      name: 'currency',
      'aria-label': t('a11y.currency_switcher'),
      autocomplete: 'off',
    }) as HTMLSelectElement;
    for (const option of snapshot.options) {
      control.appendChild(
        el('option', {
          value: option.code,
          selected: option.code === snapshot.currency,
          text: displayCurrency(option.code, snapshot),
        }),
      );
    }
    control.value = snapshot.currency;

    offChange = on(control, 'change', () => {
      const next = control.value;
      if (!isSupportedCurrency(next)) return;
      // The value to fall back to is read NOW, from the store, not captured
      // when the control was built: after one successful change the built-time
      // value is two choices stale, and reverting to it on a later failure
      // would show a currency the player never picked.
      const previous = currencySnapshot().currency;
      void setCurrency(next).catch(() => {
        control.value = previous;
      });
    });

    select = control;
    field.classList.remove('gap-0.5');
    field.replaceChildren(
      el('label', { class: 'font-medium', for: CURRENCY_SELECT_ID, text: t('common.currency') }),
      el('div', { class: 'rga-select' }, [control]),
      note(),
    );
  };

  const paint = (snapshot: CurrencySnapshot): void => {
    const codes = snapshot.options.map((option) => option.code).join(',');
    if (codes !== paintedCodes) {
      paintedCodes = codes;
      offChange?.();
      offChange = null;
      select = null;
      indicator = null;
      if (snapshot.options.length <= 1) buildIndicator(snapshot);
      else buildSelect(snapshot);
      return;
    }
    // Same options, new value: touch the value only. Rebuilding here would
    // steal focus from the control that just fired the change.
    if (select !== null) select.value = snapshot.currency;
    if (indicator !== null) indicator.textContent = displayCurrency(snapshot.currency, snapshot);
  };

  // Fires immediately with the current snapshot, which is what draws the field
  // the first time — there is no separate initial render.
  const unsubscribe = subscribeCurrency(paint);

  return {
    element: field,
    destroy: () => {
      unsubscribe();
      offChange?.();
      offChange = null;
    },
  };
}

/**
 * `EUR (€)`, or just `EUR` when the server had no distinct symbol for it.
 *
 * The symbol comes off the SNAPSHOT being painted rather than out of the
 * module-level `currencySymbol()`, so a repaint labels its options from the
 * same list it is drawing them from.
 */
function displayCurrency(code: string, snapshot: CurrencySnapshot): string {
  const symbol = snapshot.options.find((option) => option.code === code)?.symbol ?? code;
  return symbol === code ? code : `${code} (${symbol})`;
}
